import { AuthService } from './authService';
import { FilterGroupDto, CreateFilterGroupRequest, UpdateFilterGroupRequest } from '../types/filterGroup';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class FilterGroupService {
  private baseUrl = `${API_BASE_URL}/filter-groups`;

  /**
   * Get all filter groups for current tenant
   */
  async getCurrent(): Promise<FilterGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Get filter groups by type for current tenant
   */
  async getByType(filterGroupType: number): Promise<FilterGroupDto[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/current/by-type/${filterGroupType}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Get all filter groups for a specific company
   */
  async getByCompanyNo(companyNo: string): Promise<FilterGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Get filter groups by company and type
   */
  async getByCompanyAndType(companyNo: string, filterGroupType: number): Promise<FilterGroupDto[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/company/${companyNo}/by-type/${filterGroupType}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Get specific filter group by composite key
   */
  async getByCompositeKey(
    companyNo: string,
    filterGroupId: string,
    filterGroupType: number
  ): Promise<FilterGroupDto | null> {
    try {
      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/${companyNo}/${filterGroupId}/${filterGroupType}`
      );
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new filter group
   */
  async create(request: CreateFilterGroupRequest): Promise<FilterGroupDto> {
    const response = await AuthService.authenticatedPost(this.baseUrl, request);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Update an existing filter group
   */
  async update(
    companyNo: string,
    filterGroupId: string,
    filterGroupType: number,
    request: UpdateFilterGroupRequest
  ): Promise<FilterGroupDto> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/${companyNo}/${filterGroupId}/${filterGroupType}`,
      request
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Delete a filter group
   */
  async delete(companyNo: string, filterGroupId: string, filterGroupType: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${this.baseUrl}/${companyNo}/${filterGroupId}/${filterGroupType}`
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'HTTP error!'));
    }
  }

  /**
   * Check if a filter group exists
   */
  async exists(companyNo: string, filterGroupId: string, filterGroupType: number): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/${companyNo}/${filterGroupId}/${filterGroupType}`,
        {
          method: 'HEAD',
          headers: {
            Authorization: `Bearer ${AuthService.getToken()}`,
          },
        }
      );
      return response.ok;
    } catch {
      return false;
    }
  }
}

export const filterGroupService = new FilterGroupService();
